import { render } from 'react-dom';

// Mount a component off-screen — but attached to the document, so its external
// stylesheet rules still resolve via getComputedStyle — and return its root DOM
// node plus a cleanup function. Used to rasterise real components (e.g. the
// query readout) into the exported map image rather than redrawing them.
//
// `width`/`height` size the container explicitly (in px): the map export renders
// a fixed-size copy off-screen so the layout is identical on every breakpoint,
// and its Dimensions wrapper measures this container. `maxWidth` only caps width
// (used for the readout, which wraps to the map width but has its own height).
export function renderOffscreen(vnode, { width, height, maxWidth, className } = {}) {
  const container = document.createElement('div');

  // `className` lets the caller scope export-only styles: the exporter inlines each
  // element's computed style, so rules under this class are baked into the image
  // without affecting the on-screen render.
  if (className) {
    container.className = className;
  }

  container.style.position = 'absolute';
  container.style.left = '-99999px';
  container.style.top = '0';

  if (width) {
    container.style.width = `${width}px`;
  } else if (maxWidth) {
    container.style.width = `${maxWidth}px`;
  }

  if (height) {
    container.style.height = `${height}px`;
  }

  document.body.appendChild(container);
  render(vnode, container);

  return {
    node: container.firstElementChild || container,
    container,
    cleanup() {
      render(null, container);
      container.remove();
    },
  };
}

// Resolve once `selector` appears under `root`. The off-screen map renders in two
// passes — its Dimensions wrapper measures the container, then re-renders the map
// on the next tick — so the SVG isn't in the DOM the instant renderOffscreen returns.
export function waitForNode(root, selector, { timeout = 5000 } = {}) {
  return new Promise((resolve, reject) => {
    let elapsed = 0;

    const check = () => {
      const el = root.querySelector(selector);
      if (el) {
        resolve(el);
        return;
      }

      elapsed += 16;
      if (elapsed > timeout) {
        reject(new Error(`Timed out waiting for ${selector}`));
        return;
      }

      requestAnimationFrame(check);
    };

    check();
  });
}
